import React, { useEffect, useState } from "react";
import { Button, Col, DatePicker, Input, message, Modal, Row, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import {
  FIRESTORE_DEFAULT_PROJECTIONS_DOC,
  FIRESTORE_PRODUCTS_STOCK_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../constants/FirestoreConstant";
import { firestore } from "../../../../configs/FirebaseConfig";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";
import { updateProjectionsFromDate } from "urls";
import { getFirebaseIDToken } from "../../../../functions/firebase/firebase";
import axios from "axios";
import { useStockData } from "../../../../data/hooks/firestore/useStock";

const { Title } = Typography;

const DAYS_OF_WEEK = [
  { key: 0, name: "Zondag", visible: true }, // Sunday
  { key: 1, name: "Maandag", visible: true }, // Monday
  { key: 2, name: "Dinsdag", visible: true }, // Tuesday
  { key: 3, name: "Woensdag", visible: true }, // Wednesday
  { key: 4, name: "Donderdag", visible: true }, // Thursday
  { key: 5, name: "Vrijdag", visible: true }, // Friday
  { key: 6, name: "Zaterdag", visible: true }, // Saturday
];

const ProductStockModal = ({ visible, onCancel, product }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [projections, setProjections] = useState(Array(7).fill(0));
  const [currentStockInfo, setCurrentStockInfo] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const { getProductProjections, stockDates, stockData } = useStockData();

  useEffect(() => {
    if (visible && product) {
      // Get projections from context
      setProjections(getProductProjections(product.productId));

      // Calculate current stock info
      const stockInfo = calculateStockInfo();
      setCurrentStockInfo(stockInfo);
    }
  }, [visible, product, stockData]);

  const calculateStockInfo = () => {
    if (!product) return [];

    const stockInfo = [];
    stockDates.forEach((date) => {
      const data = stockData[date.id];
      if (!data) return;

      const dayOfWeek = new Date(date.dateUnix * 1000).getDay();
      const productStock = data[product.productId] || { projectedStock: 0, currentStock: 0 };

      // Calculate ordered items
      const orderedItems = productStock.projectedStock - productStock.currentStock;

      stockInfo.push({
        id: date.id,
        dateUnix: date.dateUnix,
        dayOfWeek,
        projectedStock: productStock.projectedStock,
        currentStock: productStock.currentStock,
        orderedItems,
      });
    });

    return stockInfo;
  };

  useEffect(() => {
    if (visible && product) {
      fetchProjections();
    }
  }, [visible, product]);

  const fetchProjections = async () => {
    if (!product) return;

    setLoading(true);
    try {
      // Get default projections
      const projectionDoc = await getDoc(
        doc(
          firestore,
          FIRESTORE_REGIONS_TABLE,
          COMPANY_COUNTRY_CODE,
          FIRESTORE_PRODUCTS_STOCK_TABLE,
          FIRESTORE_DEFAULT_PROJECTIONS_DOC,
        ),
      );

      let defaultProjections = [];
      if (projectionDoc.exists) {
        const data = projectionDoc.data();
        const productData = data.products.find((p) => p.id === product.productId);

        if (productData) {
          setProjections(productData.projections);
        } else {
          // If no projections exist for this product, initialize with zeros
          setProjections(Array(7).fill(0));
        }
      } else {
        // If the document doesn't exist yet
        setProjections(Array(7).fill(0));
      }

      // Get current stock data to check if changes are possible
      const stockQuery = query(
        collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_PRODUCTS_STOCK_TABLE),
        where("archived", "==", false),
      );
      const currentStockDocs = await getDocs(stockQuery);

      const stockInfo = [];
      currentStockDocs.forEach((doc) => {
        const data = doc.data();
        if (doc.id !== FIRESTORE_DEFAULT_PROJECTIONS_DOC) {
          const dayOfWeek = new Date(data.dateUnix * 1000).getDay();
          const productStock = data.products?.[product.productId] || { projectedStock: 0, currentStock: 0 };

          // Calculate ordered items (difference between projected and current)
          const orderedItems = productStock.projectedStock - productStock.currentStock;

          stockInfo.push({
            id: doc.id,
            dateUnix: data.dateUnix,
            dayOfWeek,
            projectedStock: productStock.projectedStock,
            currentStock: productStock.currentStock,
            orderedItems,
          });
        }
      });

      setCurrentStockInfo(stockInfo);
    } catch (error) {
      console.error("Error fetching projections:", error);
      message.error(t("notifications.failed_to_fetch_stock_projections"));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (index, value) => {
    const newProjections = [...projections];
    // Ensure value is a number and not less than 0
    newProjections[index] = Math.max(0, parseInt(value) || 0);
    setProjections(newProjections);
  };

  const updateProjectionsFromSelectedDate = async () => {
    if (!dateRange) {
      message.error(t("please_select_a_date"));
      return;
    }

    setLoading(true);
    try {
      const token = await getFirebaseIDToken();
      const config = { headers: { Authorization: token } };

      const startOfDayUnix = dateRange[0].startOf("day").unix();

      await axios.post(
        `${updateProjectionsFromDate}/${COMPANY_COUNTRY_CODE}`,
        {
          productId: product.productId,
          dailyProjections: projections,
          startDateUnix: startOfDayUnix,
        },
        config,
      );

      message.success(t("notifications.projections_updated_successfully"));
      onCancel();
    } catch (error) {
      console.error("Error updating projections:", error);
      message.error(t("Failed to update projections"));
      setLoading(false);
    }
  };

  if (!product) return null;

  return (
    <Modal
      title={`${t("forecast")}: ${product.productId} - ${product.productName}`}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={500}
    >
      <div style={{ marginBottom: 24 }}>
        {DAYS_OF_WEEK.filter((day) => day.visible).map((day) => (
          <Row key={day.key} align="middle" style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Title level={5} style={{ margin: 0 }}>
                {day.name}
              </Title>
            </Col>
            <Col span={12}>
              <Input
                value={projections[day.key]}
                onChange={(e) => handleInputChange(day.key, e.target.value)}
                type="number"
                min={0}
              />
            </Col>
          </Row>
        ))}
      </div>

      <Row style={{ marginBottom: 16 }}>
        <Col span={24}>
          <DatePicker
            style={{ width: "100%" }}
            placeholder={t("select_start_date_for_projection")}
            onChange={(date) => setDateRange(date ? [date] : null)}
          />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24} style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            block
            onClick={updateProjectionsFromSelectedDate}
            loading={loading}
            disabled={!dateRange}
          >
            {t("update_projections_from_selected_date")}
            <Tooltip title={t("updates_projections_tooltip")}>
              <InfoCircleOutlined style={{ marginLeft: 8 }} />
            </Tooltip>
          </Button>
        </Col>
      </Row>
    </Modal>
  );
};

export default ProductStockModal;
